import React, { memo, useState, useRef, useCallback, useEffect } from "react";
import { ZoomIn, ZoomOut, Maximize, Minimize, RotateCcw } from "lucide-react";
import { getFileExtension } from "../../../types/drawingTypes";
import { Button, Tooltip } from "../../common";
import styles from "./styles/DrawingPreview.module.css";

//#region 型定義
interface DrawingPreviewProps {
  file: File | null;           // プレビュー対象のファイル
  isLoading: boolean;          // ローディング状態
  error?: string;             // エラーメッセージ
  svgData: string | null;     // SVGプレビューデータ
}

interface Position {
  x: number;                  // X座標
  y: number;                  // Y座標
}

interface TouchInfo {
  initialDistance: number;    // ピンチ開始時の指の距離
  lastTapTime: number;       // 最後のタップ時刻
  touchCount: number;        // タッチ回数
}

// 設定定数
const CONFIG = {
  ZOOM: {
    MIN: 0.1,                // 最小倍率
    MAX: 10.0,               // 最大倍率
    STEP: 0.1,               // 倍率の変化量
    DEFAULT: 1.0,            // デフォルト倍率
    WHEEL_FACTOR: 0.1,       // ホイール操作時の倍率変化量
    DOUBLE_CLICK_SCALE: 2.0, // ダブルクリック時の拡大倍率
  },
  TOUCH: {
    THRESHOLD: 10,           // ピンチ操作の検知閾値
    DOUBLE_TAP_DELAY: 300,   // ダブルタップの判定時間（ミリ秒）
    MOVE_MARGIN: 1000,        // 移動可能範囲の余白
  },
  ANIMATION: {
    DURATION: 300,           // アニメーション時間（ミリ秒）
  }
} as const;
//#endregion

//#region ユーティリティ関数
/**
 * ファイルサイズを人間が読みやすい形式に変換
 */
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * 2点間の距離を計算
 */
const calculateTouchDistance = (touch1: React.Touch, touch2: React.Touch): number => {
  const dx = touch1.clientX - touch2.clientX;
  const dy = touch1.clientY - touch2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * アニメーション付きの値の更新
 */
const animate = (
  start: number,
  end: number,
  duration: number,
  onUpdate: (value: number) => void
): void => {
  const startTime = performance.now();
  
  const update = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    const easeProgress = 1 - Math.pow(1 - progress, 3);
    const currentValue = start + (end - start) * easeProgress;
    
    onUpdate(currentValue);
    
    if (progress < 1) {
      requestAnimationFrame(update);
    }
  };
  
  requestAnimationFrame(update);
};
//#endregion

/**
 * 図面プレビューコンポーネント
 */
export const DrawingPreview: React.FC<DrawingPreviewProps> = memo(({ 
  file, 
  isLoading, 
  error, 
  svgData 
}) => {
  //#region 状態管理
  const [scale, setScale] = useState<number>(CONFIG.ZOOM.DEFAULT);
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [lastPosition, setLastPosition] = useState<Position>({ x: 0, y: 0 });
  const [isAnimating, setIsAnimating] = useState<boolean>(false);

  const previewRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const touchInfoRef = useRef<TouchInfo>({
    initialDistance: 0,
    lastTapTime: 0,
    touchCount: 0,
  });
  //#endregion

  //#region 基本制御関数
  /**
   * 表示位置を制限する
   */
  const constrainPosition = useCallback((pos: Position): Position => {
    if (!contentRef.current || !previewRef.current) return pos;
  
    const content = contentRef.current;
    const container = previewRef.current;
    const contentRect = content.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
  
    const scaledWidth = contentRect.width * scale;
    const scaledHeight = contentRect.height * scale;
  
    // 移動範囲を拡大
    const margin = Math.max(CONFIG.TOUCH.MOVE_MARGIN, 
      Math.max(scaledWidth, scaledHeight) * 0.5); // スケールに応じて余白を動的に調整
  
    const minX = Math.min(
      -margin,
      containerRect.width - scaledWidth - margin
    );
    const minY = Math.min(
      -margin,
      containerRect.height - scaledHeight - margin
    );
    const maxX = margin;
    const maxY = margin;
  
    return {
      x: Math.max(minX, Math.min(maxX, pos.x)),
      y: Math.max(minY, Math.min(maxY, pos.y)),
    };
  }, [scale]);

  /**
   * 倍率を制限する
   */
  const clampScale = useCallback((value: number): number => {
    return Math.min(Math.max(value, CONFIG.ZOOM.MIN), CONFIG.ZOOM.MAX);
  }, []);

  /**
   * 倍率を更新する
   */
  const updateScale = useCallback((newScale: number, centerX: number, centerY: number) => {
    const clampedScale = clampScale(newScale);

    if (contentRef.current && previewRef.current) {
      const scaleFactor = clampedScale / scale;

      const newPosition = {
        x: centerX - (centerX - position.x) * scaleFactor,
        y: centerY - (centerY - position.y) * scaleFactor,
      };

      setScale(clampedScale);
      setPosition(constrainPosition(newPosition));
    } else {
      setScale(clampedScale);
    }
  }, [clampScale, scale, position, constrainPosition]);

  /**
   * 表示位置をアニメーション付きで更新
   */
  const animatePosition = useCallback((newPosition: Position) => {
    if (isAnimating) return;
    setIsAnimating(true);

    const startX = position.x;
    const startY = position.y;
    const endPos = constrainPosition(newPosition);

    animate(
      0,
      1,
      CONFIG.ANIMATION.DURATION,
      (progress) => {
        setPosition({
          x: startX + (endPos.x - startX) * progress,
          y: startY + (endPos.y - startY) * progress,
        });
      }
    );

    setTimeout(() => setIsAnimating(false), CONFIG.ANIMATION.DURATION);
  }, [position, constrainPosition, isAnimating]);

  /**
   * 倍率をアニメーション付きで更新
   */
  const animateScale = useCallback((newScale: number, centerX: number, centerY: number) => {
    if (isAnimating) return;
    setIsAnimating(true);

    const startScale = scale;
    const endScale = clampScale(newScale);

    animate(
      startScale,
      endScale,
      CONFIG.ANIMATION.DURATION,
      (currentScale) => {
        updateScale(currentScale, centerX, centerY);
      }
    );

    setTimeout(() => setIsAnimating(false), CONFIG.ANIMATION.DURATION);
  }, [scale, clampScale, updateScale, isAnimating]);
  //#endregion

  //#region マウス操作処理
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (isAnimating) return;
    e.preventDefault();
    setIsDragging(true);
    setLastPosition({ x: e.clientX, y: e.clientY });
  }, [isAnimating]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (!isDragging || isAnimating) return;

    const deltaX = e.clientX - lastPosition.x;
    const deltaY = e.clientY - lastPosition.y;

    setPosition(constrainPosition({
      x: position.x + deltaX,
      y: position.y + deltaY,
    }));

    setLastPosition({ x: e.clientX, y: e.clientY });
  }, [isDragging, lastPosition, position, constrainPosition, isAnimating]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    if (isAnimating) return;
    e.preventDefault();

    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return;

    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const newScale = scale === CONFIG.ZOOM.DEFAULT
      ? CONFIG.ZOOM.DOUBLE_CLICK_SCALE
      : CONFIG.ZOOM.DEFAULT;

    animateScale(newScale, mouseX, mouseY);
  }, [scale, isAnimating, animateScale]);
  //#endregion

  //#region タッチ操作処理
  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (isAnimating) return;
    e.preventDefault();

    const touches = e.touches;
    if (touches.length === 2) {
      touchInfoRef.current.initialDistance = calculateTouchDistance(
        touches[0],
        touches[1]
      );
    } else if (touches.length === 1) {
      const now = Date.now();
      if (now - touchInfoRef.current.lastTapTime < CONFIG.TOUCH.DOUBLE_TAP_DELAY) {
        const rect = previewRef.current?.getBoundingClientRect();
        if (!rect) return;

        const touch = touches[0];
        const touchX = touch.clientX - rect.left;
        const touchY = touch.clientY - rect.top;

        const newScale = scale === CONFIG.ZOOM.DEFAULT
          ? CONFIG.ZOOM.DOUBLE_CLICK_SCALE
          : CONFIG.ZOOM.DEFAULT;

        animateScale(newScale, touchX, touchY);
      }
      touchInfoRef.current.lastTapTime = now;
      setIsDragging(true);
      setLastPosition({ x: touches[0].clientX, y: touches[0].clientY });
    }
  }, [scale, isAnimating, animateScale]);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (isAnimating) return;
    e.preventDefault();

    const touches = e.touches;
    if (touches.length === 2) {
      const currentDistance = calculateTouchDistance(touches[0], touches[1]);
      const scaleFactor = currentDistance / touchInfoRef.current.initialDistance;
      
      const rect = previewRef.current?.getBoundingClientRect();
      if (!rect) return;

      const centerX = (touches[0].clientX + touches[1].clientX) / 2 - rect.left;
      const centerY = (touches[0].clientY + touches[1].clientY) / 2 - rect.top;

      const newScale = clampScale(scale * scaleFactor);
      updateScale(newScale, centerX, centerY);

      touchInfoRef.current.initialDistance = currentDistance;
    } else if (touches.length === 1 && isDragging) {
      const touch = touches[0];
      const deltaX = touch.clientX - lastPosition.x;
      const deltaY = touch.clientY - lastPosition.y;

      setPosition(constrainPosition({
        x: position.x + deltaX,
        y: position.y + deltaY,
      }));

      setLastPosition({ x: touch.clientX, y: touch.clientY });
    }
  }, [isDragging, lastPosition, position, scale, constrainPosition, clampScale, updateScale, isAnimating]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);
  //#endregion

  //#region ホイール操作処理
  const handleWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    if (isAnimating) return;
    event.preventDefault();

    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return;

    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    const delta = -Math.sign(event.deltaY) * CONFIG.ZOOM.WHEEL_FACTOR;
    const newScale = scale + delta;

    if (Math.abs(delta) < 0.2) {
      updateScale(newScale, mouseX, mouseY);
    } else {
      animateScale(newScale, mouseX, mouseY);
    }
  }, [scale, updateScale, animateScale, isAnimating]);
  //#endregion

  //#region 制御ボタン処理
  const handleZoomIn = useCallback((e: React.MouseEvent) => {
    if (isAnimating) return;
    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    animateScale(scale + CONFIG.ZOOM.STEP, centerX, centerY);
  }, [scale, animateScale, isAnimating]);

  const handleZoomOut = useCallback((e: React.MouseEvent) => {
    if (isAnimating) return;
    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    animateScale(scale - CONFIG.ZOOM.STEP, centerX, centerY);
  }, [scale, animateScale, isAnimating]);

  const handleReset = useCallback(() => {
    if (isAnimating) return;
    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    animateScale(CONFIG.ZOOM.DEFAULT, centerX, centerY);
    animatePosition({ x: 0, y: 0 });
  }, [animateScale, animatePosition, isAnimating]);
  //#endregion

  //#region キーボード操作処理
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!previewRef.current || isAnimating) return;

      const rect = previewRef.current.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      switch (e.key) {
        case '+':
        case '=':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            animateScale(scale + CONFIG.ZOOM.STEP, centerX, centerY);
          }
          break;
        case '-':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            animateScale(scale - CONFIG.ZOOM.STEP, centerX, centerY);
          }
          break;
        case '0':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleReset();
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          animatePosition({ x: position.x + 50, y: position.y });
          break;
        case 'ArrowRight':
          e.preventDefault();
          animatePosition({ x: position.x - 50, y: position.y });
          break;
        case 'ArrowUp':
          e.preventDefault();
          animatePosition({ x: position.x, y: position.y + 50 });
          break;
        case 'ArrowDown':
          e.preventDefault();
          animatePosition({ x: position.x, y: position.y - 50 });
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [scale, position, animateScale, animatePosition, handleReset, isAnimating]);
  //#endregion

  //#region UIレンダリング関数
  const renderFileInfo = useCallback(() => {
    if (!file) return null;

    return (
      <div className={styles.fileInfo}>
        <div className="space-y-1">
          <p className="text-xs text-gray-600">
            <span className="font-medium">ファイル名:</span> {file.name}
          </p>
          <p className="text-xs text-gray-600">
            <span className="font-medium">ファイルタイプ:</span>{" "}
            {getFileExtension(file.name).toUpperCase()}
          </p>
          <p className="text-xs text-gray-600">
            <span className="font-medium">ファイルサイズ:</span>{" "}
            {formatFileSize(file.size)}
          </p>
          <p className="text-xs text-gray-600">
            <span className="font-medium">表示倍率:</span>{" "}
            {(scale * 100).toFixed(0)}%
          </p>
        </div>
      </div>
    );
  }, [file, scale]);

  const renderZoomControls = useCallback(() => (
    <div className={styles.controls}>
      <Tooltip content="拡大 (Ctrl/Cmd +)" side="left">
        <Button
          variant="secondary"
          size="small"
          onClick={handleZoomIn}
          disabled={scale >= CONFIG.ZOOM.MAX || isAnimating}
          className="!h-8 !w-8 !p-0 flex items-center justify-center bg-white"
          aria-label="拡大"
        >
          <ZoomIn className="h-4 w-4" />
        </Button>
      </Tooltip>

      <Tooltip content="縮小 (Ctrl/Cmd -)" side="left">
        <Button
          variant="secondary"
          size="small"
          onClick={handleZoomOut}
          disabled={scale <= CONFIG.ZOOM.MIN || isAnimating}
          className="!h-8 !w-8 !p-0 flex items-center justify-center bg-white"
          aria-label="縮小"
        >
          <ZoomOut className="h-4 w-4" />
        </Button>
      </Tooltip>

      <Tooltip content="リセット (Ctrl/Cmd 0)" side="left">
        <Button
          variant="secondary"
          size="small"
          onClick={handleReset}
          disabled={
            (scale === CONFIG.ZOOM.DEFAULT &&
              position.x === 0 &&
              position.y === 0) ||
            isAnimating
          }
          className="!h-8 !w-8 !p-0 flex items-center justify-center bg-white"
          aria-label="リセット"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
      </Tooltip>
    </div>
  ), [scale, position, handleZoomIn, handleZoomOut, handleReset, isAnimating]);

  const renderLoading = useCallback(() => (
    <div className="flex justify-center items-center h-full">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
    </div>
  ), []);

  const renderError = useCallback(() => (
    <div className="flex justify-center items-center h-full text-red-500">
      <div className="text-center">
        <p className="font-medium">エラーが発生しました</p>
        {error && <p className="text-sm mt-2">{error}</p>}
      </div>
    </div>
  ), [error]);

  const renderGuide = useCallback(() => {
    if (!svgData) return null;

    return (
      <div className={styles.guide}>
        <details className="text-xs text-gray-600">
          <summary className="cursor-pointer hover:text-gray-900 transition-colors">
            操作方法を表示
          </summary>
          <div className="mt-2 space-y-1">
            <p>• ドラッグ: 図面を移動</p>
            <p>• マウスホイール/ピンチ: 拡大・縮小</p>
            <p>• ダブルクリック/タップ: 拡大・縮小の切り替え</p>
            <p>• Ctrl/Cmd + Plus: 拡大</p>
            <p>• Ctrl/Cmd + Minus: 縮小</p>
            <p>• Ctrl/Cmd + 0: 表示をリセット</p>
            <p>• 矢印キー: 図面を移動</p>
          </div>
        </details>
      </div>
    );
  }, [svgData]);

  const renderContent = useCallback(() => {
    if (isLoading) return renderLoading();
    if (error) return renderError();
    if (!file) {
      return (
        <div className="flex justify-center items-center h-full text-gray-500">
          <p className="text-center">
            ファイルを選択するとプレビューが表示されます
          </p>
        </div>
      );
    }

    return (
      <div
        className={`${styles.previewContainer} ${
          isDragging ? styles.cursorGrabbing : styles.cursorGrab
        }`}
        ref={previewRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        role="application"
        aria-label="図面プレビュー"
      >
        <div
          ref={contentRef}
          className={`${styles.previewContent} ${isDragging ? styles.dragging : ""}`}
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          }}
          dangerouslySetInnerHTML={{ __html: svgData || "" }}
        />

        {renderZoomControls()}

        {/* ズーム倍率表示 */}
        <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm rounded-md shadow px-2 py-1 text-sm text-gray-600">
          {(scale * 100).toFixed(0)}%
        </div>
      </div>
    );
  }, [
    isLoading,
    error,
    file,
    svgData,
    isDragging,
    scale,
    position,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleDoubleClick,
    handleWheel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    renderZoomControls
  ]);
  //#endregion

  //#region メインレンダリング
  return (
    <div className="h-full flex flex-col">
      {renderFileInfo()}
      <div className="flex-1 relative overflow-hidden bg-gray-50">
        {renderContent()}
      </div>
      {renderGuide()}
    </div>
  );
  //#endregion
});

// DisplayNameの設定
DrawingPreview.displayName = "DrawingPreview";

export default DrawingPreview;